import apiClient from './api';
import { RaceEvent, ComplianceStatus, ComplianceRequirementStatus } from '../types';

/**
 * Service module for race event API operations.
 */

export interface RaceFilters {
  yearFrom?: number;
  yearTo?: number;
  category?: string;
}

/** API response format for race events (snake_case from backend) */
interface RaceEventApiResponse {
  race_id: string;
  name: string;
  year: number;
  category: string;
  date?: string;
  requirements_count: number;
  created_at: string;
}

/** API response format for compliance (snake_case from backend) */
interface ComplianceApiResponse {
  race_id: string;
  race_name: string;
  category: string;
  total_crew: number;
  overall_compliance_percentage: number;
  all_requirements_met: boolean;
  crew_requirements: Array<{
    requirement_id: string;
    description: string;
    type: string;
    status: string;
    required_quantity?: number;
    current_count?: number;
    required_percentage?: number;
    current_percentage?: number;
    verified_crew?: string[];
    missing_crew?: string[];
  }>;
  yacht_requirements: Array<{
    requirement_id: string;
    description: string;
    type: string;
    status: string;
    documents?: Array<{
      document_id: string;
      document_type: string;
      verification_status: string;
    }>;
  }>;
  required_documents?: Record<string, unknown>;
  document_summary?: Record<string, unknown>;
}

/** Transform snake_case API response to camelCase frontend type */
function transformRaceEvent(apiEvent: RaceEventApiResponse): RaceEvent {
  return {
    raceId: apiEvent.race_id,
    name: apiEvent.name,
    year: apiEvent.year,
    category: apiEvent.category,
    date: apiEvent.date,
    requirements: [],  // Summary endpoint doesn't include full requirements
    documents: [],
    createdAt: apiEvent.created_at,
  };
}

/** Transform compliance API response to frontend type */
function transformCompliance(apiResponse: ComplianceApiResponse): ComplianceStatus {
  const crewRequirements: ComplianceRequirementStatus[] = (apiResponse.crew_requirements || []).map(req => ({
    requirementId: req.requirement_id,
    description: req.description,
    type: 'crew_qualification' as const,
    met: req.status === 'met',
    requiredCount: req.required_quantity,
    verifiedCount: req.current_count,
    requiredPercentage: req.required_percentage,
    percentage: req.current_percentage,
  }));

  const yachtRequirements: ComplianceRequirementStatus[] = (apiResponse.yacht_requirements || []).map(req => ({
    requirementId: req.requirement_id,
    description: req.description,
    type: 'yacht_certification' as const,
    met: req.status === 'met',
  }));

  return {
    raceId: apiResponse.race_id,
    allMet: apiResponse.all_requirements_met,
    crewRequirements,
    yachtRequirements,
    expiringDocuments: [], // Backend doesn't return this yet
  };
}

export const raceService = {
  /** Fetch all race events, optionally filtered by year range or category. */
  getRaces: async (filters?: RaceFilters): Promise<RaceEvent[]> => {
    const params = new URLSearchParams();
    if (filters?.yearFrom) params.append('yearFrom', String(filters.yearFrom));
    if (filters?.yearTo) params.append('yearTo', String(filters.yearTo));
    if (filters?.category) params.append('category', filters.category);
    const response = await apiClient.get<{ races: RaceEventApiResponse[] }>('/races', { params });
    return response.data.races.map(transformRaceEvent);
  },

  /** Fetch compliance status for a specific race event. */
  getCompliance: async (raceId: string): Promise<ComplianceStatus> => {
    const response = await apiClient.get<ComplianceApiResponse>(`/races/${raceId}/compliance`);
    return transformCompliance(response.data);
  },

  /** Download a PDF compliance report for a race event. */
  getComplianceReport: async (raceId: string): Promise<string> => {
    const response = await apiClient.get<{ url: string }>(`/compliance/report/${raceId}`);
    return response.data.url;
  },
};
